import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { demandApi, type DemandItem, type FilterOptions } from '@/services/api';
import FilterBar from './components/FilterBar';
import ListingTable from '@/pages/supply/components/ListingTable';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MessageModal from '@/components/feature/MessageModal';
import { useMessages } from '@/hooks/useMessages';
import { formatFieldValue } from '@/components/feature/GreenCertFieldConfig';

type SortKey = 'newest' | 'quantity_desc' | 'quantity_asc' | 'price_asc' | 'price_desc';

const PAGE_SIZE = 8;

export default function DemandPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const csv = (k: string) => {
    const v = searchParams.get(k);
    return v ? v.split(',') : [];
  };
  const [year, setYear] = useState<string[]>(csv('year'));
  const [projectType, setProjectType] = useState<string[]>(csv('projectType'));
  const [channel, setChannel] = useState<string[]>(csv('channel'));
  const [subsidy, setSubsidy] = useState<string[]>(csv('subsidy'));
  const [tradeMethod, setTradeMethod] = useState<string[]>(csv('tradeMethod'));
  const [exchange, setExchange] = useState<string[]>(csv('exchange'));
  const [province, setProvince] = useState<string[]>(csv('province'));
  const [sort, setSort] = useState<SortKey>((searchParams.get('sort') as SortKey) || 'newest');
  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);

  const [items, setItems] = useState<DemandItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [messageTarget, setMessageTarget] = useState<DemandItem | null>(null);
  const { sendMessage } = useMessages();

  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    years: [],
    projectTypes: [],
    channels: [],
    provinceRequirements: [],
    subsidies: [],
    tradeMethods: [],
    tradePrinciples: [],
    exchanges: [],
    provinces: [],
  });

  useEffect(() => {
    demandApi
      .options()
      .then((opts) => setFilterOptions(opts))
      .catch(() => {});
  }, []);

  // Sync state → URL
  useEffect(() => {
    const next: Record<string, string> = {};
    if (year.length) next.year = year.join(',');
    if (projectType.length) next.projectType = projectType.join(',');
    if (channel.length) next.channel = channel.join(',');
    if (subsidy.length) next.subsidy = subsidy.join(',');
    if (tradeMethod.length) next.tradeMethod = tradeMethod.join(',');
    if (exchange.length) next.exchange = exchange.join(',');
    if (province.length) next.province = province.join(',');
    if (sort !== 'newest') next.sort = sort;
    if (page > 1) next.page = String(page);
    setSearchParams(next, { replace: true });
  }, [year, projectType, channel, subsidy, tradeMethod, exchange, province, sort, page, setSearchParams]);

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    demandApi
      .list({
        year: year.join(',') || undefined,
        projectType: projectType.join(',') || undefined,
        channel: channel.join(',') || undefined,
        subsidy: subsidy.join(',') || undefined,
        tradeMethod: tradeMethod.join(',') || undefined,
        exchange: exchange.join(',') || undefined,
        provinceRequirement: province.join(',') || undefined,
        sort,
        page,
        pageSize: PAGE_SIZE,
      })
      .then((result) => {
        setItems(result.list);
        setTotal(result.total);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : '加载失败');
        setLoading(false);
      });
  }, [year, projectType, channel, subsidy, tradeMethod, exchange, province, sort, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleReset = () => {
    setYear([]);
    setProjectType([]);
    setChannel([]);
    setSubsidy([]);
    setTradeMethod([]);
    setExchange([]);
    setProvince([]);
    setSort('newest');
    setPage(1);
    setSearchParams({});
  };

  return (
    <>
      <Navbar />
      <div className="w-full bg-background-50 min-h-screen">
        {/* Page header */}
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">
                绿证采购需求
              </h1>
              <p className="text-foreground-500 text-sm mt-2">
                浏览各行业企业发布的绿证采购需求信息，找到与您匹配的买方资源
              </p>
            </div>
            <Link
              to="/demand-publish"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer shrink-0"
            >
              <i className="ri-add-line"></i>
              发布采购需求
            </Link>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          {/* Filter bar */}
          <FilterBar
            year={year}
            onYearChange={setYear}
            projectType={projectType}
            onProjectTypeChange={setProjectType}
            channel={channel}
            onChannelChange={setChannel}
            subsidy={subsidy}
            onSubsidyChange={setSubsidy}
            tradeMethod={tradeMethod}
            onTradeMethodChange={setTradeMethod}
            exchange={exchange}
            onExchangeChange={setExchange}
            province={province}
            onProvinceChange={setProvince}
            sort={sort}
            onSortChange={setSort}
            onReset={handleReset}
            totalCount={total}
            yearOptions={filterOptions.years.map((v) => ({ value: v, label: `${v}年度` }))}
            projectTypeOptions={filterOptions.projectTypes}
            channelOptions={filterOptions.channels}
            subsidyOptions={filterOptions.subsidies}
            tradeMethodOptions={filterOptions.tradeMethods}
            exchangeOptions={filterOptions.exchanges}
            provinceOptions={filterOptions.provinces}
          />

          {/* Loading */}
          {loading && (
            <div className="text-center py-20">
              <div className="w-12 h-12 mx-auto rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div>
              <p className="text-foreground-500 text-sm mt-4">正在加载...</p>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 mt-4">{error}</p>
              <button
                onClick={fetchData}
                className="mt-3 text-sm text-primary-600 hover:text-primary-700 cursor-pointer font-medium"
              >
                重新加载
              </button>
            </div>
          )}

          {/* Results */}
          {!loading && !error && items.length > 0 && (
            <>
              <ListingTable items={items} kind="demand" onMessageClick={(it) => setMessageTarget(it as DemandItem)} />

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="w-9 h-9 rounded-md border border-background-300 flex items-center justify-center text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label="上一页"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-9 h-9 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                        page === p
                          ? 'bg-primary-500 text-background-50'
                          : 'text-foreground-600 hover:bg-background-100 border border-background-300'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="w-9 h-9 rounded-md border border-background-300 flex items-center justify-center text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label="下一页"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </>
          )}

          {!loading && !error && items.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-file-search-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 mt-4">未找到符合条件的需求信息</p>
              <button
                onClick={handleReset}
                className="mt-3 text-sm text-primary-600 hover:text-primary-700 cursor-pointer font-medium"
              >
                重置筛选条件
              </button>
            </div>
          )}
        </div>
      </div>
      <Footer />
      {messageTarget && (
        <MessageModal
          open={!!messageTarget}
          onClose={() => setMessageTarget(null)}
          listingId={messageTarget.id}
          listingType="demand"
          listingTitle={`${messageTarget.company} - ${formatFieldValue('projectType', messageTarget.projectType)}`}
          receiverName={messageTarget.company}
          receiverId={messageTarget.userId ?? 0}
          onSend={(content) => {
            if (!messageTarget.userId) return;
            sendMessage(messageTarget.userId, {
              listingId: messageTarget.id,
              listingType: 'demand',
              listingTitle: `${messageTarget.company} - ${formatFieldValue('projectType', messageTarget.projectType)}`,
              receiverId: messageTarget.userId,
              receiverName: messageTarget.company,
              content,
            });
          }}
        />
      )}
    </>
  );
}